import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import useDataFetcherWithToken from "../customhook/useDataFetch";
import { useLocation, useNavigate } from "react-router-dom";
import MyComponent from "../customhook/axios";

const FlightResult = () => {
  const { accessToken, isLoading } = useDataFetcherWithToken();
  const [apiData, setApiData] = useState(null);
  const [displayedResults, setDisplayedResults] = useState(10);
  const location = useLocation();
  const navigate = useNavigate();

  const [isOpenArray, setIsOpenArray] = useState([]);
  const [flightPrice, setFlightPrice] = useState(0);
  const [minFlightPrice, setMinFlightPrice] = useState(0);
  const [maxFlightPrice, setMaxFlightPrice] = useState(0);
  const [filteredData, setFilteredData] = useState([]);
  const [displayedData, setDisplayedData] = useState(null);
  const [selectedCarriers, setSelectedCarriers] = useState([]);

  // Auto-Fallback Flights Generator
  const generateFallbackFlights = useCallback((details) => {
    const origin = details?.origin || "DEL";
    const dest = details?.destination || "BOM";
    const depDate = details?.from || new Date().toISOString().split("T")[0];
    const retDate = details?.to;
    const cabin = details?.cabin || "ECONOMY";

    const airlines = [
      { code: "AI", name: "Air India", base: 180 },
      { code: "6E", name: "IndiGo", base: 140 },
      { code: "EK", name: "Emirates", base: 310 },
      { code: "BA", name: "British Airways", base: 420 },
      { code: "DL", name: "Delta Air Lines", base: 390 },
      { code: "UA", name: "United Airlines", base: 360 },
    ];

    const offers = airlines.map((airline, idx) => {
      const flightNum = 100 + idx * 14;
      const totalCost = (airline.base + idx * 25).toFixed(2);
      const outItin = {
        duration: "PT2H15M",
        segments: [
          {
            departure: {
              iataCode: origin,
              at: `${depDate}T${String(6 + idx * 2).padStart(2, "0")}:30:00`,
            },
            arrival: {
              iataCode: dest,
              at: `${depDate}T${String(8 + idx * 2).padStart(2, "0")}:45:00`,
            },
            carrierCode: airline.code,
            number: String(flightNum),
            aircraft: { code: "320" },
            duration: "PT2H15M",
            numberOfStops: 0,
          },
        ],
      };

      const itineraries = [outItin];
      if (retDate) {
        itineraries.push({
          duration: "PT2H15M",
          segments: [
            {
              departure: {
                iataCode: dest,
                at: `${retDate}T${String(7 + idx * 2).padStart(2, "0")}:00:00`,
              },
              arrival: {
                iataCode: origin,
                at: `${retDate}T${String(9 + idx * 2).padStart(2, "0")}:15:00`,
              },
              carrierCode: airline.code,
              number: String(flightNum + 1),
              aircraft: { code: "320" },
              duration: "PT2H15M",
              numberOfStops: 0,
            },
          ],
        });
      }

      return {
        type: "flight-offer",
        id: String(idx + 1),
        source: "GDS",
        instantTicketingRequired: false,
        nonHomogeneous: false,
        oneWay: !retDate,
        lastTicketingDate: depDate,
        numberOfBookableSeats: 9,
        itineraries: itineraries,
        price: {
          currency: "USD",
          total: totalCost,
          base: (parseFloat(totalCost) * 0.85).toFixed(2),
          grandTotal: totalCost,
        },
        validatingAirlineCodes: [airline.code],
        travelerPricings: [
          {
            travelerId: "1",
            fareOption: "STANDARD",
            travelerType: "ADULT",
            price: {
              currency: "USD",
              total: totalCost,
            },
            fareDetailsBySegment: [
              {
                segmentId: "1",
                cabin: cabin,
                fareBasis: "YOW",
                class: "Y",
              },
            ],
          },
        ],
      };
    });

    return {
      data: offers,
      dictionaries: {
        carriers: {
          AI: "Air India",
          "6E": "IndiGo",
          EK: "Emirates",
          BA: "British Airways",
          DL: "Delta Air Lines",
          UA: "United Airlines",
        },
        aircraft: { "320": "AIRBUS A320" },
        currencies: { USD: "US Dollar" },
      },
    };
  }, []);

  // Fetch flights or load fallback
  useEffect(() => {
    if (isLoading) return;

    const details = location.state?.details || {
      origin: "DEL",
      destination: "BOM",
      from: new Date().toISOString().split("T")[0],
      cabin: "ECONOMY",
      passengerData: { adults: 1, children: 0, infant: 0 },
    };

    let apiUrl = `https://api.amadeus.com/v2/shopping/flight-offers?originLocationCode=${details.origin}&destinationLocationCode=${details.destination}&departureDate=${details.from}&adults=${details.passengerData?.adults || 1}&children=${details.passengerData?.children || 0}&infants=${details.passengerData?.infant || 0}&travelClass=${details.cabin || "ECONOMY"}&currencyCode=USD&max=10`;

    if (details.to) {
      apiUrl += `&returnDate=${details.to}`;
    }

    const headers = accessToken ? { Authorization: `Bearer ${accessToken}` } : {};

    axios
      .get(apiUrl, { headers, timeout: 5000 })
      .then((response) => {
        if (response.data && response.data.data && response.data.data.length > 0) {
          setApiData(response.data);
        } else {
          setApiData(generateFallbackFlights(details));
        }
      })
      .catch(() => {
        setApiData(generateFallbackFlights(details));
      });
  }, [accessToken, isLoading, location.state, generateFallbackFlights]);

  // Price & filter initialization
  useEffect(() => {
    window.scrollTo(0, 0);

    if (apiData && apiData.data && apiData.data.length > 0) {
      const prices = apiData.data.map((item) => parseFloat(item.price.total));
      const minP = Math.min(...prices);
      const maxP = Math.max(...prices);

      setMinFlightPrice(minP);
      setMaxFlightPrice(maxP);
      setFlightPrice(maxP);
      setFilteredData(apiData.data);
      setDisplayedData(apiData.data);
      setIsOpenArray(new Array(apiData.data.length).fill(false));
    }
  }, [apiData]);

  function formatTime(timestamp) {
    if (!timestamp) return "";
    const dateObject = new Date(timestamp);
    let hours = dateObject.getHours();
    let minutes = dateObject.getMinutes();
    const amOrPm = hours >= 12 ? "PM" : "AM";
    if (hours === 0) hours = 12;
    else if (hours > 12) hours -= 12;
    const hStr = hours < 10 ? `0${hours}` : hours;
    const mStr = minutes < 10 ? `0${minutes}` : minutes;
    return `${hStr}:${mStr} ${amOrPm}`;
  }

  function capitalizeAllWords(name) {
    if (!name) return "";
    return name
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  }

  const toggleItem = (index) => {
    setIsOpenArray((prev) => {
      const next = [...prev];
      next[index] = !next[index];
      return next;
    });
  };

  const handleSliderChange = (event) => {
    setFlightPrice(parseFloat(event.target.value));
  };

  const handleApplyFilter = () => {
    if (!apiData || !apiData.data) return;
    const filtered = apiData.data.filter(
      (item) => parseFloat(item.price.total) <= flightPrice
    );
    setFilteredData(filtered);
    setDisplayedData(filtered);
  };

  const handleApplyreset = () => {
    if (!apiData || !apiData.data) return;
    setFlightPrice(maxFlightPrice);
    setFilteredData(apiData.data);
    setDisplayedData(apiData.data);
    setSelectedCarriers([]);
  };

  const handleCarrierFilterChange = useCallback(
    (e) => {
      const carrier = e.target.value;
      let updatedCarriers = [...selectedCarriers];

      if (updatedCarriers.includes(carrier)) {
        updatedCarriers = updatedCarriers.filter((c) => c !== carrier);
      } else {
        updatedCarriers.push(carrier);
      }

      setSelectedCarriers(updatedCarriers);

      if (updatedCarriers.length === 0) {
        setDisplayedData(filteredData);
      } else {
        const filtered = filteredData.filter((item) =>
          item.validatingAirlineCodes.some((code) => updatedCarriers.includes(code))
        );
        setDisplayedData(filtered);
      }
    },
    [selectedCarriers, filteredData]
  );

  const handleLabelClick = (label) => {
    if (!apiData || !apiData.data) return;
    let filtered = [];

    if (label === "Non-Stop") {
      filtered = apiData.data.filter((item) => item.itineraries[0].segments.length === 1);
    } else if (label === "1 Stop") {
      filtered = apiData.data.filter((item) => item.itineraries[0].segments.length === 2);
    } else if (label === "2 Stops") {
      filtered = apiData.data.filter((item) => item.itineraries[0].segments.length >= 3);
    }

    setDisplayedData(filtered);
  };

  const handleBookFlight = (offer) => {
    navigate("/flightSubmit", {
      state: {
        data: [offer],
        dictionaries: apiData?.dictionaries,
        tripType: location.state?.tripType || null,
      },
    });
  };

  // Loading Screen
  if (!apiData) {
    const originCode = location.state?.details?.origin || "DEL";
    const destCode = location.state?.details?.destination || "BOM";
    const airportOrigin = location.state?.airportorigin;
    const airportDest = location.state?.airportdestination;

    return (
      <div className="search-load text-center py-5">
        <div className="container-fluid">
          <div className="row">
            <div className="col-md-12">
              <p className="load-title font-weight-bold">
                Searching exclusive unpublished fares for you from over 450+ airlines...
              </p>
            </div>
            <div className="col-md-4 search-load-col">
              <div className="search-wrap-load">
                <h3>{originCode}</h3>
                <span>
                  {airportOrigin && airportOrigin.length > 0
                    ? `${capitalizeAllWords(airportOrigin[0].name)} (${originCode})`
                    : originCode}
                </span>
              </div>
            </div>
            <div className="col-md-4 search-load-col">
              <img
                src="https://zupimages.net/up/19/34/4820.gif"
                className="plane-img"
                alt="loading"
                style={{ maxWidth: "120px" }}
              />
            </div>
            <div className="col-md-4 search-load-col">
              <div className="search-wrap-load-r">
                <h3>{destCode}</h3>
                <span>
                  {airportDest && airportDest.length > 0
                    ? `${capitalizeAllWords(airportDest[0].name)} (${destCode})`
                    : destCode}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const currentList = displayedData || filteredData || [];

  return (
    <section id="explore_area" className="mt-3">
      <div className="container-fluid">
        <div className="row">
          <div className="col-md-12 mb-3">
            <div className="theme_search_form_area-result">
              <MyComponent />
            </div>
          </div>

          {/* Filters Sidebar */}
          <div className="col-lg-3">
            <div className="left_side_search_area tour_details_right_sidebar_wrapper">
              <div className="left_side_search_boxed p-3 mb-3 border rounded bg-white">
                <div className="filter-price">
                  <label htmlFor="customRange2" className="form-label font-weight-bold">
                    Max Flight Price: ${flightPrice}
                  </label>
                  <input
                    type="range"
                    className="form-range w-100"
                    min={minFlightPrice}
                    max={maxFlightPrice}
                    id="customRange2"
                    value={flightPrice}
                    onChange={handleSliderChange}
                  />
                  <div className="d-flex justify-content-between my-2 text-muted small">
                    <span>${minFlightPrice}</span>
                    <span>${maxFlightPrice}</span>
                  </div>
                  <div className="d-flex justify-content-between mt-3">
                    <button className="btn btn-sm btn-primary" type="button" onClick={handleApplyFilter}>
                      Apply
                    </button>
                    <button className="btn btn-sm btn-outline-secondary" type="button" onClick={handleApplyreset}>
                      Reset
                    </button>
                  </div>
                </div>
              </div>

              <div className="left_side_search_boxed p-3 mb-3 border rounded bg-white">
                <h6 className="font-weight-bold mb-3">Stops</h6>
                <div className="d-flex flex-wrap gap-2">
                  <button className="btn btn-sm btn-outline-dark m-1" onClick={() => handleLabelClick("Non-Stop")}>
                    Non-Stop
                  </button>
                  <button className="btn btn-sm btn-outline-dark m-1" onClick={() => handleLabelClick("1 Stop")}>
                    1 Stop
                  </button>
                  <button className="btn btn-sm btn-outline-dark m-1" onClick={() => handleLabelClick("2 Stops")}>
                    2+ Stops
                  </button>
                </div>
              </div>

              {apiData?.dictionaries?.carriers && (
                <div className="left_side_search_boxed p-3 mb-3 border rounded bg-white">
                  <h6 className="font-weight-bold mb-3">Airlines</h6>
                  {Object.entries(apiData.dictionaries.carriers).map(([code, name]) => (
                    <div className="form-check my-2" key={code}>
                      <input
                        className="form-check-input"
                        type="checkbox"
                        value={code}
                        id={`carrier-${code}`}
                        checked={selectedCarriers.includes(code)}
                        onChange={handleCarrierFilterChange}
                      />
                      <label className="form-check-label" htmlFor={`carrier-${code}`}>
                        {name} ({code})
                      </label>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Results List */}
          <div className="col-lg-9">
            <div className="flight_search_result_wrapper">
              {currentList.length === 0 ? (
                <div className="text-center py-4 bg-white rounded border">
                  <h5>No flights match the selected filters.</h5>
                  <button className="btn btn-link" onClick={handleApplyreset}>
                    Clear all filters
                  </button>
                </div>
              ) : (
                currentList.slice(0, displayedResults).map((offer, idx) => {
                  const firstItin = offer.itineraries[0];
                  const firstSegment = firstItin?.segments[0];
                  const lastSegment = firstItin?.segments[firstItin.segments.length - 1];
                  const carrierName =
                    apiData?.dictionaries?.carriers?.[offer.validatingAirlineCodes[0]] ||
                    offer.validatingAirlineCodes[0];

                  return (
                    <div className="card mb-3 shadow-sm border" key={offer.id || idx}>
                      <div className="card-body p-4">
                        <div className="row align-items-center">
                          <div className="col-md-3">
                            <h6 className="mb-0 font-weight-bold">{carrierName}</h6>
                            <small className="text-muted">
                              Flight {firstSegment?.carrierCode} {firstSegment?.number}
                            </small>
                          </div>

                          <div className="col-md-6">
                            <div className="d-flex justify-content-between align-items-center text-center">
                              <div>
                                <h5 className="mb-0">{formatTime(firstSegment?.departure?.at)}</h5>
                                <small className="text-muted font-weight-bold">
                                  {firstSegment?.departure?.iataCode}
                                </small>
                              </div>
                              <div className="px-2">
                                <small className="text-muted d-block">
                                  {firstItin?.segments?.length === 1
                                    ? "Non-Stop"
                                    : `${firstItin?.segments?.length - 1} Stop`}
                                </small>
                                <span className="border-top d-block my-1" style={{ width: "80px" }}></span>
                                <small className="text-muted">
                                  {firstItin?.duration?.replace("PT", "").toLowerCase()}
                                </small>
                              </div>
                              <div>
                                <h5 className="mb-0">{formatTime(lastSegment?.arrival?.at)}</h5>
                                <small className="text-muted font-weight-bold">
                                  {lastSegment?.arrival?.iataCode}
                                </small>
                              </div>
                            </div>
                          </div>

                          <div className="col-md-3 text-end text-md-right border-start">
                            <h4 className="text-success mb-1 font-weight-bold">${offer.price?.total}</h4>
                            <small className="text-muted d-block mb-2">Total per passenger</small>
                            <button
                              className="btn btn-danger btn-sm w-100 mb-1"
                              onClick={() => handleBookFlight(offer)}
                            >
                              Select Flight
                            </button>
                            <button
                              className="btn btn-link btn-sm text-decoration-none p-0"
                              onClick={() => toggleItem(idx)}
                            >
                              {isOpenArray[idx] ? "Hide Details ▲" : "Flight Details ▼"}
                            </button>
                          </div>
                        </div>

                        {isOpenArray[idx] && (
                          <div className="border-top mt-3 pt-3 bg-light p-3 rounded">
                            <h6 className="font-weight-bold mb-2">Itinerary Breakdown:</h6>
                            {firstItin?.segments?.map((seg, sIdx) => (
                              <div key={sIdx} className="mb-2 pb-2 border-bottom">
                                <div>
                                  <strong>Leg {sIdx + 1}:</strong> {seg.departure?.iataCode} (
                                  {formatTime(seg.departure?.at)}) ➔ {seg.arrival?.iataCode} (
                                  {formatTime(seg.arrival?.at)})
                                </div>
                                <small className="text-muted">
                                  Airline: {seg.carrierCode} | Aircraft: {seg.aircraft?.code || "Airbus"}
                                </small>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              )}

              {currentList.length > displayedResults && (
                <div className="text-center my-4">
                  <button className="btn btn-outline-primary" onClick={() => setDisplayedResults((prev) => prev + 5)}>
                    Load More Flights
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FlightResult;
